import {
    from,
    lastValueFrom,
    map,
    Observable,
    shareReplay,
    take,
} from 'rxjs';

import { createRestClient } from '@superbet-group/web.lib.rest';

import { logger } from '@app/logger';
import { BetBuilderMarkets } from '../types/BetBuilderMarkets';
import { appLocale } from '@src/config/locale';
import { getCurrentTarget } from './betBuilderConfig';
import {
    BetBuilderOdd,
    BetBuilderOddStatus,
} from '../types/BetBuilderOdd';
import { BetBuilderPreview } from '../types/BetBuilderPreview';
import { defaultHttpTimeoutMs } from '@core/constants';
import {
    BetBuilderSseConnection,
    getBetBuilderSseConnection,
} from './common/betBuilderSseConnection';
import {
    CreateBetBuilderOddDto,
    CreateBetBuilderOddV2Dto,
} from '../types/dtos/CreateBetBuilderOddDto';
import OfferEvent from '@core/models/offer/OfferEvent';
import {
    transformCreateBetBuilderOdd,
    transformCreateBetBuilderOddV2,
} from '../mappers/transformCreateBetBuilderOdd';
import BetSlipOdd from '@core/models/base/BetSlipOdd';
import { getSportOfferConfig } from '../../config/sportOfferConfig';
import { getBetBuilderSseConnectionRegistry } from './common/betBuilderSseConnectionRegistry';
import { transformBetBuilderMarkets } from '../mappers/transformBetBuilderMarkets';
import { BetBuilderMarketsDto } from '../types/dtos/BetBuilderMarketsDto';
import { BetBuilderEventsDto } from '../types/dtos/BetBuilderEventsDto';
import { featureFlagService } from '@superbet-group/web.lib.flags';
import { SgaOddPriceDto } from '../types/dtos/SgaOddPriceDto';
import { transformSgaOddPrice } from '../mappers/transformSgaOddPrice';

const BetBuilderVersion = {
    v1: 'v1',
    v2: 'v2',
} as const;
type BetBuilderVersion = typeof BetBuilderVersion[keyof typeof BetBuilderVersion];

type BetBuilderEventsCache = {
    events$?: Observable<BetBuilderEventsDto>;
    timestamp?: number;
};

const betBuilderEventsCacheDurationMs = 3 * 60 * 1000; // 3 minutes

export function createBetBuilderService(
    host: string,
    streamingHost: string,
    version: BetBuilderVersion,
) {
    const languageCode = appLocale.locale;
    const target = getCurrentTarget();
    const restClient = createRestClient(host, defaultHttpTimeoutMs, false);
    const marketsSseConnection = getBetBuilderSseConnection<BetBuilderMarketsDto>(
        `${streamingHost}/${version}/stream/getBetbuilderMarketsForMatch`,
    );
    const betbuilderMarketsSseConnections = getBetBuilderSseConnectionRegistry<BetBuilderMarketsDto>(
        `${streamingHost}/${version}/stream/getBetbuilderMarketsForMatch`,
    );
    const oddsSseConnection = getBetBuilderSseConnection<BetBuilderPreview>(
        `${streamingHost}/${version}/stream/previewBetbuilderOdd`,
    );
    const betbuilderOddUpdatesSseConnections: Record<OfferEvent['id'], BetBuilderSseConnection<BetBuilderPreview>> = {};
    const betslipOddUpdatesSseConnections = getBetBuilderSseConnectionRegistry<CreateBetBuilderOddDto | CreateBetBuilderOddV2Dto>(
        `${streamingHost}/${version}/stream/trackBetbuilderOddStatus`,
    );
    const sgaOddPriceUpdatesSseConnections = getBetBuilderSseConnectionRegistry<SgaOddPriceDto>(
        `${streamingHost}/${version}/stream/getSgaOddPrice`,
    );

    let betbuilderMarketsSubscriptionCallback: ((data: BetBuilderMarkets) => void) | null = null;
    let betslipOddUpdatesSubscriptionCallback: ((eventId: OfferEvent['id'], data: BetSlipOdd) => void) | null = null;

    const betBuilderEventsCache: BetBuilderEventsCache = {};

    function isSgaOddPriceEnabled(): Promise<boolean> {
        return featureFlagService.getFeatureValue('sports-offer.sga-rearch.phase-IV-enabled', false);
    }

    function fetchBetBuilderEventIds() {
        const url = `${host}/${version}/getBetbuilderEvents?target=${target}`;

        return from(restClient.get<BetBuilderEventsDto>(url));
    }

    function getBetBuilderEventIds(): Nullable<Promise<string[]>> {
        try {
            // if cache is NOT valid, initiate a new fetch
            if (
                !betBuilderEventsCache.events$
            || !betBuilderEventsCache.timestamp
            || (Date.now() - betBuilderEventsCache.timestamp > betBuilderEventsCacheDurationMs)
            ) {
                betBuilderEventsCache.timestamp = Date.now();
                /**
                 * By leveraging the Share Replay operator,
                 * we can cache API responses in memory and serve them to multiple subscribers,
                 * saving valuable network resources and significantly reducing the loading time of our application.
                 * [How it works](https://medium.com/@jhonaraldy/optimizing-api-calls-with-rxjs-leveraging-the-share-replay-operator-for-efficient-data-retrieval-3c211041ecdb#:~:text=of%20our%20application.-,How%20it%20works,-%3A)
                 */
                betBuilderEventsCache.events$ = fetchBetBuilderEventIds().pipe(shareReplay());
            }

            return lastValueFrom(
                betBuilderEventsCache.events$.pipe(
                    take(1),
                    map(({ events }) => events),
                ),
            );
        } catch (e) {
            return null;
        }
    }

    async function isEventBetBuilder(eventId: OfferEvent['id']): Promise<boolean> {
        const betbuilderEventIds = await getBetBuilderEventIds();

        return Boolean(betbuilderEventIds?.includes(String(eventId)));
    }

    async function getBetBuilderMarkets(eventId: OfferEvent['id']): Promise<BetBuilderMarkets> {
        try {
            const betbuilderEvents = await getBetBuilderEventIds();

            if (betbuilderEvents && !betbuilderEvents.includes(String(eventId))) {
                return {
                    matchId: String(eventId),
                    markets: [],
                };
            }

            const url = `${host}/${version}/getBetbuilderMarketsForMatch?match_id=${eventId}&lang=${languageCode}&target=${target}`;

            const response = await restClient.get<BetBuilderMarketsDto>(url);

            return removeUnavailableMarkets(
                transformBetBuilderMarkets(response),
            );
        } catch (error) {
            logger.error(error as Error);

            return {
                matchId: String(eventId),
                markets: [],
            };
        }
    }

    function liveUpdateBetBuilderMarkets(eventId: OfferEvent['id']): void {
        const params = {
            match_id: `${eventId}`,
            lang: languageCode,
            target,
        };

        marketsSseConnection.establishConnection(params);
    }

    // TODO: unite with liveUpdateBetBuilderMarkets
    async function liveUpdateMEBBMarkets(eventId: OfferEvent['id']): Promise<BetBuilderMarkets> {
        const data = await getBetBuilderMarkets(eventId);

        if (data.markets.length) {
            const params = {
                match_id: `${eventId}`,
                lang: languageCode,
                target,
            };

            betbuilderMarketsSseConnections.establishConnectionForKey(`${eventId}`, params);

            betbuilderMarketsSubscriptionCallback?.(data);
        }

        return data;
    }

    function previewBetBuilderCustomOdd(
        eventId: OfferEvent['id'],
        selectedOddUuids: BetBuilderOdd['uuid'][],
        sessionId: string,
    ): Promise<BetBuilderPreview> {
        const sortedUuids = Array.from(selectedOddUuids).sort().join(',');
        const url = `${host}/${version}/previewBetbuilderOdd?match_id=${eventId}&selected_odds_uuids=${sortedUuids}&lang=${languageCode}&target=${target}`;
        return restClient.get<BetBuilderPreview>(url, {
            headers: {
                sessionId,
            },
        });
    }

    function liveUpdatePreviewBetBuilderCustomOdd(
        eventId: OfferEvent['id'],
        selectedOddUuids: BetBuilderOdd['uuid'][],
        sessionId: string,
    ): void {
        const sortedUuids = Array.from(selectedOddUuids).sort().join(',');
        const params = {
            match_id: `${eventId}`,
            selected_odds_uuids: sortedUuids,
            lang: languageCode,
            target,
        };

        oddsSseConnection.establishConnection(params, { sessionId });
    }

    // TODO: unite with liveUpdatePreviewBetBuilderCustomOdd
    function liveUpdatePreviewMEBBCustomOdd(
        eventId: OfferEvent['id'],
        selectedOddUuids: BetBuilderOdd['uuid'][],
        sessionId: string,
    ): void {
        const sortedUuids = Array.from(selectedOddUuids).sort().join(',');
        const params = {
            match_id: `${eventId}`,
            selected_odds_uuids: sortedUuids,
            lang: languageCode,
            target,
        };

        if (!betbuilderOddUpdatesSseConnections[eventId]) {
            betbuilderOddUpdatesSseConnections[eventId] = getBetBuilderSseConnection<BetBuilderPreview>(
                `${streamingHost}/${version}/stream/previewBetbuilderOdd`,
            );
        }

        betbuilderOddUpdatesSseConnections[eventId].establishConnection(params, { sessionId });
    }

    async function _getSgaOddPrice(
        eventId: OfferEvent['id'],
        oddUuids: BetBuilderOdd['uuid'][],
    ): Promise<BetSlipOdd> {
        const url = `${host}/${version}/getSgaOddPrice?match_id=${eventId}&selected_odds_uuids=${oddUuids.join(',')}&lang=${languageCode}&target=${target}`;
        const response = await restClient.get<SgaOddPriceDto>(url);

        return transformSgaOddPrice(
            response,
        );
    }

    async function _buildBetbuilderOdd(
        eventId: OfferEvent['id'],
        oddUuids: BetBuilderOdd['uuid'][],
        sessionId: string,
        // TODO: Remove event & fallbackMarketName params (with a lot of other stuff) during stale Offer v1 cleanup
        event: OfferEvent,
        fallbackMarketName: string,
    ): Promise<BetSlipOdd> {
        const url = `${host}/${version}/buildBetbuilderOdd?match_id=${eventId}&selected_odds_uuids=${oddUuids.join(',')}&lang=${languageCode}&target=${target}`;
        const response = await restClient.post<void, CreateBetBuilderOddDto | CreateBetBuilderOddV2Dto>(url, undefined, {
            headers: {
                sessionId,
            },
        });

        if (version === BetBuilderVersion.v2) {
            return transformCreateBetBuilderOddV2(
                response as CreateBetBuilderOddV2Dto,
            );
        } else {
            return transformCreateBetBuilderOdd(
                response as CreateBetBuilderOddDto,
                event,
                fallbackMarketName,
            );
        }
    }

    async function createBet(
        eventId: OfferEvent['id'],
        selectedOddUuids: BetBuilderOdd['uuid'][],
        sessionId: string,
        // TODO: Remove event & fallbackMarketName params (with a lot of other stuff) during stale Offer v1 cleanup
        event: OfferEvent,
        fallbackMarketName: string,
    ): Promise<BetSlipOdd> {
        const sortedUuids = Array.from(selectedOddUuids).sort();

        // In case of singular odd, it's not even a bet builder thing, we can just
        // use the simple odd :)
        if (sortedUuids.length === 1) {
            return event.getOddByUuid(sortedUuids[0])!;
        }

        const odd = await isSgaOddPriceEnabled()
            ? await _getSgaOddPrice(eventId, sortedUuids)
            : await _buildBetbuilderOdd(eventId, sortedUuids, sessionId, event, fallbackMarketName);

        if (!odd.legs) {
            return odd;
        } else {
            const legMap = new Map(odd.legs.map((leg) => [leg.oddUuid, leg]));

            if (selectedOddUuids.some((uuid) => !legMap.has(uuid))) {
                throw new Error('Fetch SGA Odd Price did not return legs for all selectedUuids');
            }

            const legs = selectedOddUuids.map((uuid) => legMap.get(uuid)!);

            return {
                ...odd,
                legs,
            };
        }
    }

    async function _trackBetbuilderOdd(
        eventId: OfferEvent['id'],
        oddUuid: BetSlipOdd['uuid'],
        // TODO: Remove event & fallbackMarketName params (with a lot of other stuff) during stale Offer v1 cleanup
        event: OfferEvent | undefined,
        fallbackMarketName: string,
    ): Promise<BetSlipOdd> {
        const url = `${host}/${version}/trackBetbuilderOddStatus?match_id=${eventId}&odd_uuid=${oddUuid}&lang=${languageCode}&target=${target}`;
        const response = await restClient.get<CreateBetBuilderOddDto | CreateBetBuilderOddV2Dto>(url);

        if (version === BetBuilderVersion.v2) {
            return transformCreateBetBuilderOddV2(
                response as CreateBetBuilderOddV2Dto,
            );
        } else {
            return transformCreateBetBuilderOdd(
                response as CreateBetBuilderOddDto,
                event,
                fallbackMarketName,
            );
        }
    }

    /**
     * Using `_buildBetbuilderOdd` a BetSlipOdd is created with an unique uuid that references a specific leg combination.
     * That uuid is used when calling `_trackBetbuilderOdd`.
     *
     * By introducing _getSgaOddPrice, the `BetSlipOdd uuid` will no longer reference a specific leg combination.
     * Instead, the `BetBuilderOdd uuids` that define the legs are sent.
     *
     * In case there are no `bbOddUuids` available, it is expected that the `BetSlipOdd uuid` is valid and `_trackBetbuilderOdd` will be called.
     * When the `bbOddUuids` are available, the Sga Odd Price FF is checked.
     * If enabled, `_getSgaOddPrice` is called.
     * Otherwise, `_trackBetbuilderOdd` is used if by chance the `BetSlipOdd uuid` is valid.
     * As a fallback, a new BetSlipOdd is created by calling `_buildBetbuilderOdd` with the `bbOddUuids`.
     *
     * https://axilis.atlassian.net/browse/BMB-1978
     */
    async function getCreatedOdd(
        eventId: OfferEvent['id'],
        oddUuid: BetSlipOdd['uuid'],
        // TODO: Remove event & fallbackMarketName params (with a lot of other stuff) during stale Offer v1 cleanup
        event: OfferEvent,
        fallbackMarketName: string,
        sessionId?: string,
        bbOddUuids?: BetBuilderOdd['uuid'][],
    ): Promise<BetSlipOdd | undefined> {
        if (!bbOddUuids?.every(Boolean)) {
            return _trackBetbuilderOdd(eventId, oddUuid, event, fallbackMarketName);
        } else {
            const sortedUuids = Array.from(bbOddUuids).sort();

            if (await isSgaOddPriceEnabled()) {
                return _getSgaOddPrice(eventId, sortedUuids);
            } else {
                try {
                    return await _trackBetbuilderOdd(eventId, oddUuid, event, fallbackMarketName);
                } catch {
                    if (!sessionId) {
                        // It has been agreed to let this fail as this is a rare edge case or a transitioning period
                        throw new Error('User is not logged in while attempting to recrate a betslip odd with an invalid uuid!');
                    }

                    return _buildBetbuilderOdd(
                        eventId,
                        sortedUuids,
                        sessionId,
                        event,
                        fallbackMarketName,
                    );
                }
            }
        }
    }

    async function liveUpdateCreateOdd(
        eventId: OfferEvent['id'],
        oddUuid: BetSlipOdd['uuid'],
        // TODO: Remove event & fallbackMarketName params (with a lot of other stuff) during stale Offer v1 cleanup
        event: OfferEvent,
        fallbackMarketName: string,
        sessionId?: string,
        bbOddUuids?: BetBuilderOdd['uuid'][],
    ): Promise<void> {
        if (bbOddUuids?.every(Boolean) && await isSgaOddPriceEnabled()) {
            sgaOddPriceUpdatesSseConnections.establishConnectionForKey(`${eventId}`, {
                match_id: `${eventId}`,
                selected_odds_uuids: bbOddUuids.join(','),
                target,
                lang: languageCode,
            });
        } else {
            betslipOddUpdatesSseConnections.establishConnectionForKey(`${eventId}`, {
                match_id: `${eventId}`,
                odd_uuid: oddUuid,
                target,
                lang: languageCode,
            });
        }

        // Explanation: When we subscribe to the odd, we don't have a guarantee of latest
        // state. If we don't make an extra call, we might be in an inconsistent state, so
        // let's fetch the initial state.
        // Example: Make BB odd, leave page, come back 5 minutes later, it rehydrates from local storage,
        // but updated might not be forthcoming.
        // TODO: Remove event & fallbackMarketName params (with a lot of other stuff) during stale Offer v1 cleanup
        const odd = await getCreatedOdd(eventId, oddUuid, event, fallbackMarketName, sessionId, bbOddUuids);

        if (odd) {
            betslipOddUpdatesSubscriptionCallback?.(eventId, odd);
        }
    }

    function subscribeToBetBuilderMarketsUpdates(
        callback: (data: BetBuilderMarkets) => void,
    ): void {
        marketsSseConnection.subscribe((data) => {
            callback(transformBetBuilderMarkets(data));
        });
    }

    // TODO: unite with subscribeToBetBuilderMarketsUpdates
    function subscribeToMEBBMarketsUpdates(
        callback: (data: BetBuilderMarkets) => void,
    ): void {
        betbuilderMarketsSubscriptionCallback = callback;
        betbuilderMarketsSseConnections.subscribe(({ data }) => callback(transformBetBuilderMarkets(data)));
    }

    function subscribeToBetBuilderCustomOddUpdates(
        callback: (data: BetBuilderPreview) => void,
    ): void {
        oddsSseConnection.subscribe(callback);
    }

    // TODO: unite with subscribeToBetBuilderCustomOddUpdates
    function subscribeToMEBBCustomOddUpdates(
        eventId: OfferEvent['id'],
        callback: (data: BetBuilderPreview) => void,
    ): void {
        betbuilderOddUpdatesSseConnections[eventId].subscribe(callback);
    }

    function subscribeToBetBuilderCreatedOddUpdates(
        callback: (eventId: OfferEvent['id'], data: BetSlipOdd) => void,
        getOfferEventById: (eventId: OfferEvent['id']) => Promise<OfferEvent | undefined>,
        getMarketNameFallbackForEvent: (eventId: OfferEvent['id']) => string,
    ): void {
        betslipOddUpdatesSubscriptionCallback = callback;

        sgaOddPriceUpdatesSseConnections.subscribe((message) => {
            try {
                const eventId = parseInt(message.key, 10);
                const odd = transformSgaOddPrice(message.data as SgaOddPriceDto);

                callback(eventId, odd);
            } catch {
                // We can safely ignore the error and assume that it's logged
                // elsewhere. The error can, at worst, cause the event odd value
                // to be out of sync with the actual value. Given that BB is a
                // prematch feature, this is not a big deal.
            }
        });

        betslipOddUpdatesSseConnections.subscribe(async (message) => {
            try {
                const eventId = parseInt(message.key, 10);
                const event = await getOfferEventById(eventId);
                const odd = version === BetBuilderVersion.v2
                    ? transformCreateBetBuilderOddV2(message.data as CreateBetBuilderOddV2Dto)
                    : transformCreateBetBuilderOdd(
                        message.data as CreateBetBuilderOddDto,
                        event,
                        getMarketNameFallbackForEvent(eventId),
                    );

                callback(eventId, odd);
            } catch {
                // We can safely ignore the error and assume that it's logged
                // elsewhere. The error can, at worst, cause the event odd value
                // to be out of sync with the actual value. Given that BB is a
                // prematch feature, this is not a big deal.
            }
        });
    }

    function unsubscribeFromBetBuilderMarketsUpdates() {
        marketsSseConnection.disconnect();
    }

    // TODO: unite with unsubscribeFromBetBuilderMarketsUpdates
    function unsubscribeFromMEBBMarketsUpdates(eventId: OfferEvent['id']) {
        betbuilderMarketsSseConnections.disconnectForKey(`${eventId}`);
    }

    function unsubscribeFromBetBuilderCustomOddUpdates() {
        oddsSseConnection.disconnect();
    }

    // TODO: unite with unsubscribeFromBetBuilderCustomOddUpdates
    function unsubscribeFromMEBBCustomOddUpdates(eventId: OfferEvent['id']) {
        betbuilderOddUpdatesSseConnections[eventId]?.disconnect();
    }

    function unsubscribeFromBetBuilderCreatedOddUpdatesForEvent(eventId: OfferEvent['id']): void {
        sgaOddPriceUpdatesSseConnections.disconnectForKey(String(eventId));
        betslipOddUpdatesSseConnections.disconnectForKey(String(eventId));
    }

    return {
        createBet,
        getCreatedOdd,
        liveUpdateCreateOdd,
        isSgaOddPriceEnabled,
        isEventBetBuilder,
        getBetBuilderMarkets,
        liveUpdateBetBuilderMarkets,
        liveUpdateMEBBMarkets,
        previewBetBuilderCustomOdd,
        liveUpdatePreviewBetBuilderCustomOdd,
        liveUpdatePreviewMEBBCustomOdd,
        subscribeToBetBuilderMarketsUpdates,
        subscribeToMEBBMarketsUpdates,
        subscribeToBetBuilderCustomOddUpdates,
        subscribeToMEBBCustomOddUpdates,
        subscribeToBetBuilderCreatedOddUpdates,
        unsubscribeFromBetBuilderMarketsUpdates,
        unsubscribeFromMEBBMarketsUpdates,
        unsubscribeFromBetBuilderCustomOddUpdates,
        unsubscribeFromMEBBCustomOddUpdates,
        unsubscribeFromBetBuilderCreatedOddUpdatesForEvent,
    };
}

const {
    serviceUrl,
    streamingServiceUrl,
} = getSportOfferConfig();

export const betBuilderService = createBetBuilderService(
    serviceUrl,
    streamingServiceUrl,
    BetBuilderVersion.v2,
);

function removeUnavailableMarkets(data: BetBuilderMarkets): BetBuilderMarkets {
    return {
        matchId: data.matchId,
        markets: data.markets.filter((market) => market.odds.some((odd) => odd.status !== BetBuilderOddStatus.Unavailable)),
    };
}
